import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import { itemsApi } from '../api/api';

const UNITS = ['Piece','Kg','Gram','Meter','Feet','Liter','Box','Bag','Set','Pair','Roll','Sheet'];
const EMPTY = { item_code:'', name:'', category:'', unit:'Piece', unit_price:'', reorder_qty:'', stock_qty:'', hsn_code:'', description:'', status:'active' };

export default function ItemMaster() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['items', search, statusFilter],
    queryFn: () => itemsApi.getAll({ search, status: statusFilter }),
  });

  const saveMutation = useMutation({
    mutationFn: (data) => editing ? itemsApi.update(editing.id, data) : itemsApi.create(data),
    onSuccess: () => { qc.invalidateQueries(['items']); qc.invalidateQueries(['dashboard']); toast.success(editing ? 'Item updated' : 'Item created'); closeModal(); },
    onError: (e) => toast.error(e.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: itemsApi.delete,
    onSuccess: () => { qc.invalidateQueries(['items']); toast.success('Item deleted'); },
    onError: (e) => toast.error(e.error || 'Failed to delete'),
  });

  const openAdd = () => { setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = (item) => { setEditing(item); setForm({...item}); setModal(true); };
  const closeModal = () => { setModal(false); setEditing(null); };
  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const submit = (e) => { e.preventDefault(); saveMutation.mutate(form); };

  return (
    <>
      <div className="topbar">
        <span className="topbar-title">Item Master</span>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Item</button>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <Package size={15} />
            <span className="card-title">Items ({items.length})</span>
            <div className="search-wrap">
              <Search /><input placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="table-wrap">
            {isLoading ? <div className="spinner" /> : (
              <table>
                <thead><tr>
                  <th>Code</th><th>Name</th><th>Category</th><th>Unit</th>
                  <th>Unit Price</th><th>Stock Qty</th><th>Reorder Qty</th><th>HSN</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {items.length === 0 && <tr><td colSpan={10}><div className="empty-state"><Package /><p>No items found</p></div></td></tr>}
                  {items.map(item => (
                    <tr key={item.id}>
                      <td className="mono">{item.item_code}</td>
                      <td style={{fontWeight: 500}}>{item.name}</td>
                      <td>{item.category || '—'}</td>
                      <td>{item.unit}</td>
                      <td className="amount">₹{Number(item.unit_price).toLocaleString('en-IN')}</td>
                      <td style={{color: item.stock_qty <= item.reorder_qty ? 'var(--red)' : undefined, fontWeight: item.stock_qty <= item.reorder_qty ? 600 : 400}}>{item.stock_qty}</td>
                      <td>{item.reorder_qty}</td>
                      <td className="mono">{item.hsn_code || '—'}</td>
                      <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                      <td>
                        <div style={{display:'flex',gap:4}}>
                          <button className="btn-icon" onClick={() => openEdit(item)}><Pencil /></button>
                          {/* <button className="btn-icon danger" onClick={() => { if(window.confirm('Delete this item?')) deleteMutation.mutate(item.id); }}><Trash2 /></button>
                      */}  </div> 
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit Item' : 'Add New Item'}</h2>
              <button className="btn btn-sm" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={submit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Item Code *</label>
                    <input className="form-control" required value={form.item_code} onChange={e => set('item_code', e.target.value)} placeholder="e.g. ITM-001" disabled={!!editing} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Item Name *</label>
                    <input className="form-control" required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Item name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <input className="form-control" value={form.category} onChange={e => set('category', e.target.value)} placeholder="e.g. Raw Material" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit *</label>
                    <select className="form-control" required value={form.unit} onChange={e => set('unit', e.target.value)}>
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit Price (₹)</label>
                    <input className="form-control" type="number" min="0" step="0.01" value={form.unit_price} onChange={e => set('unit_price', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Stock Quantity</label>
                    <input className="form-control" type="number" min="0" value={form.stock_qty} onChange={e => set('stock_qty', e.target.value)} placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reorder Quantity</label>
                    <input className="form-control" type="number" min="0" value={form.reorder_qty} onChange={e => set('reorder_qty', e.target.value)} placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">HSN Code</label>
                    <input className="form-control" value={form.hsn_code} onChange={e => set('hsn_code', e.target.value)} placeholder="e.g. 73069090" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="form-group form-full">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Optional description" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving...' : editing ? 'Update Item' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}